import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CustomTooltip.css';

const SidebarTooltip = ({ children, message, position, delay = 500, theme = 'dark', arrow = true, maxWidth = '200px', className = '', redirectUrl = null, clickToRedirect = false, showActionButton = false, actionButtonText, actionButtonUrl, onActionClick = null, interactive = false }) => {
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const timeoutRef = useRef(null);
    const tooltipRef = useRef(null);
    const triggerRef = useRef(null);

    const showTooltip = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            calculatePosition();
            setIsVisible(true);
        }, delay);
    };

    const hideTooltip = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        
        if (interactive) {
            timeoutRef.current = setTimeout(() => {
                setIsVisible(false);
            }, 100);
        } else {
            setIsVisible(false);
        }
    };

    const handleTriggerClick = () => {
        if (clickToRedirect && redirectUrl) {
            navigate(redirectUrl);
        }
    };

    const handleActionClick = () => {
        if (onActionClick) {
            onActionClick();
        } else if (actionButtonUrl) {
            navigate(actionButtonUrl);
        }
        setIsVisible(false);
    };

    const handleTooltipMouseEnter = () => {
        if (interactive && timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    const handleTooltipMouseLeave = () => {
        if (interactive) {
            setIsVisible(false);
        }
    };

    const calculatePosition = () => {
        if (!triggerRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        let top, left;

        switch (position) {
            case 'top':
                top = triggerRect.top - 10;
                left = triggerRect.left + triggerRect.width / 2;
                break;
            case 'bottom':
                top = triggerRect.bottom + 10;
                left = triggerRect.left + triggerRect.width / 2;
                break;
            case 'left':
                top = triggerRect.top + triggerRect.height / 2;
                left = triggerRect.left - 10;
                break;
            case 'right':
            default:
                top = triggerRect.top + triggerRect.height / 2;
                left = triggerRect.right + 10;
                break;
        }

        // Boundary checks
        if (left + 200 > viewportWidth) {
            left = triggerRect.left - 210;
        }
        if (top + 50 > viewportHeight) {
            top = viewportHeight - 60;
        }
        if (top < 10) {
            top = 10;
        }
        if (left < 10) {
            left = 10;
        }

        setTooltipPosition({ top, left });
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="custom-tooltip-wrapper">
            <div
                ref={triggerRef}
                onMouseEnter={showTooltip}
                onMouseLeave={hideTooltip}
                onFocus={showTooltip}
                onBlur={hideTooltip}
                onClick={handleTriggerClick}
                className={`tooltip-trigger ${clickToRedirect ? 'clickable' : ''}`}
                style={{ cursor: clickToRedirect ? 'pointer' : 'default' }}
            >
                {children}
            </div>

            {isVisible && message && (
                <div
                    ref={tooltipRef}
                    className={`custom-tooltip ${theme} ${position} ${arrow ? 'with-arrow' : ''} ${interactive ? 'interactive' : ''} ${className}`}
                    style={{
                        top: `${tooltipPosition.top}px`,
                        left: `${tooltipPosition.left}px`,
                        maxWidth: maxWidth,
                        position: 'fixed',
                        zIndex: 10000
                    }}
                    onMouseEnter={handleTooltipMouseEnter}
                    onMouseLeave={handleTooltipMouseLeave}
                >
                    {arrow && <div className={`tooltip-arrow ${position}`} />}
                    <div className="tooltip-content">
                        <div className="tooltip-message">{message}</div>
                        {showActionButton && (
                            <button
                                className="tooltip-action-button"
                                onClick={handleActionClick}
                                type="button"
                            >
                                {actionButtonText}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SidebarTooltip;